import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Accessor,
  Bars,
  CategoryAxis,
  categoryScale,
  ChartProps,
  InteractionItem,
  LinearAxis,
  linearScale,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { ChartWrapper } from './ChartWrapper';

export type PercentageFailureEntry = {
  name: string;
  uniqueId: string;
  totalFailedExecutions: number;
  totalExecutions: number;
  failurePercentage: number;
  isDeleted: boolean;
};

type PerformanceFailureChartProps = {
  xAxisTitle: string;
  items: PercentageFailureEntry[] | undefined;
  getTooltip: (entry: PercentageFailureEntry) => React.ReactNode;
  isLoading: boolean;
  error: string | undefined;
  missingDataMessage: string;
  testId?: string;
};

export const PercentageFailureChart: FC<PerformanceFailureChartProps> = ({
  xAxisTitle: yAxisTitle,
  items,
  isLoading,
  getTooltip,
  error,
  missingDataMessage,
  testId = 'percentage-failure-chart',
}) => {
  const getResourceDetailsPageUrl = useLinkToExplorer('resourceDetails');
  const navigate = useNavigate();
  const { accountId, projectId } = useDbtCloudProject();
  const projectUrlParams = useUrlProjectParams();

  const xAccessor: Accessor<PercentageFailureEntry, string> = (
    x: PercentageFailureEntry,
  ) => x.name;

  const chartProps: ChartProps = useMemo<ChartProps>(() => {
    const props: ChartProps = {
      margin: { left: 50, right: 5, top: 20, bottom: 40 },
      height: 350,
      xScale: categoryScale({ labels: items?.map(xAccessor) || [] }),
      yScale: linearScale({ min: 0, max: 100 }),
      xAccessor,
      yAccessor: (x: PercentageFailureEntry) => {
        return x.failurePercentage || 0;
      },
      onItemClick(item: InteractionItem<PercentageFailureEntry>) {
        const nodeIsDeleted = item?.item?.isDeleted;
        if (nodeIsDeleted) return;

        const url = getResourceDetailsPageUrl({
          dbtUniqueId: item?.item?.uniqueId || '',
          projectId: projectId || 0,
          accountId: accountId || 0,
          tab: 'performance',
          environmentType: projectUrlParams.environmentType,
        });
        navigate(url);
      },
      tooltip: ({
        interactionItem: x,
      }: TooltipCallbackArgs<PercentageFailureEntry>) => {
        if (!x) return null;
        return <div className="text-xs">{getTooltip(x.item)}</div>;
      },
      testId,
    };
    return props;
  }, [
    items,
    testId,
    getResourceDetailsPageUrl,
    projectId,
    accountId,
    projectUrlParams.environmentType,
    navigate,
    getTooltip,
  ]);

  return (
    <div className="mt-3 w-full">
      {chartProps && (
        <ChartWrapper
          missingDataStyle="success"
          numDataPoints={items?.length}
          noDataMessage={missingDataMessage}
          {...chartProps}
          isLoading={isLoading}
          height={350}
          error={error}
        >
          <CategoryAxis axis="x" />
          <LinearAxis axis="y" label={yAxisTitle} />
          <Plot
            data={items ?? []}
            itemComponent={Bars}
            extraItemProps={{
              className:
                'fill-fgVizRed transition-all hover:fill-fgVizRedHover hover:cursor-pointer',
            }}
          />
        </ChartWrapper>
      )}
    </div>
  );
};
